import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth';
import { useToastStore } from './stores/toast';
import { queryClient } from './lib/queryClient';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/password-reset/index';
import PasswordResetVerify from './pages/password-reset/verify';
import AccountRestore from './pages/account/restore';
import SignupTypeSelect from './pages/signup/index';
import SignupUsername from './pages/signup/username';
import SignupEmail from './pages/signup/email';
import SignupEmailComplete from './pages/signup/email-complete';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';

// Sites pages
import SitesList from './pages/sites/index';
import SiteDetail from './pages/sites/SiteDetail';
import SiteForm from './pages/sites/SiteForm';
import AgencyLinkConnectPage from './pages/agency-links/AgencyLinkConnectPage';
import AgencyLinkRequestsPage from './pages/agency-links/AgencyLinkRequestsPage';
import AgencyLinkRequestDetailPage from './pages/agency-links/AgencyLinkRequestDetailPage';

// Inspections pages (적응형 레이아웃)
import InspectionsListAdaptive from './pages/inspections/index.adaptive';
import InspectionDetailAdaptive from './pages/inspections/[id].adaptive';
import NewInspection from './pages/inspections/new';

// Invite page
import AcceptInvitation from './pages/invite/[token]';

// Reports pages
import ReportsList from './pages/reports/index';
import NewReport from './pages/reports/new';
import NewComposed from './pages/reports/new-composed';
import TbmDistributionSetupPage from './pages/reports/[id]/tbm-distribution-setup';
// 보고서 상세·인쇄 미리보기 — 라우트가 Composer 화면을 직접 마운트한다(갈래 하나 · 중간 디스패처를 두지 않는다).
// 두 화면은 lazy로 둔다(Suspense fallback은 null — 인쇄 계약 `.print-ready`/`#print-meta`는 ComposedPrintPreview가
// 준비 뒤에만 노출하므로 Puppeteer·e2e가 빈 화면을 오인하지 않는다. 로딩 중 표시를 넣지 말 것 — 그 문구가 print
// selector·본문 텍스트 단언에 섞인다).
const ComposerReportDetail = lazy(() => import('./pages/reports/[id]/ComposerReportDetail'));
const ComposedPrintPreview = lazy(() => import('./pages/reports/[id]/composed-print-preview'));

// TBM pages
import TbmEducatePage from './pages/tbm/[id]/educate';
import TBMSign from './pages/tbm/sign/[token]';
import EducatorPage from './pages/tbm/educator/[token]';

// External Approval page (공개)
import ExternalApproval from './pages/approval/[token]';

// Delete Approval page (공개 - 삭제 승인)
import DeleteApprovalPage from './pages/delete-approval/[token]';

// Contact Signature page (공개 - 보고서 현장 담당자 서명, SMS 링크)
import ContactSignaturePage from './pages/contact-signature/[token]';

// Share page (공개 - 토큰으로 보고서 조회)
import SharedReportPage from './pages/share/[token]';

// Schedules pages
import SchedulesPage from './pages/schedules/index';

// Approvals pages
import ApprovalsPage from './pages/approvals/index';

// Legal documents pages (법정 서류 관리 — `legal-obligation` 플래그 뒤, 진입 게이트는 LegalDocumentsGate)
import LegalDocumentsGate from './pages/legal-documents/LegalDocumentsGate';
import LegalDocumentsSitesPage from './pages/legal-documents/index';
import SiteObligationsPage from './pages/legal-documents/SiteObligationsPage';
import AgencyOverlaysPage from './pages/legal-documents/AgencyOverlaysPage';
import InstanceRedirectPage from './pages/legal-documents/InstanceRedirectPage';
import PastSitePage from './pages/legal-documents/PastSitePage';
import OrganizationObligationsPage from './pages/legal-documents/OrganizationObligationsPage';

// Notifications page
import NotificationsPage from './pages/notifications/index';

// Settings pages
import OrganizationSettings from './pages/settings/Organization';
import ApprovalSettings from './pages/settings/Approval';
import BillingSettings from './pages/settings/Billing';
import AccountSettings from './pages/settings/Account';
import GroupsSettings from './pages/settings/Groups';
import RolesSettings from './pages/settings/Roles';

// Members page
import MembersPage from './pages/members/index';

// Organization Info page
import OrganizationInfo from './pages/OrganizationInfo';

// Profile page
import ProfilePage from './pages/profile/index';

// Auth pages
import ImpersonatePage from './pages/auth/ImpersonatePage';
import ClaimCompany from './pages/auth/ClaimCompany';

// Release notes (public)
import ReleaseNotesPage from './pages/release-notes/index';

// 공지 — 게시판(권한 없음)과 조직 저작(`announcement.manage`)이 **다른 화면**이다
import AnnouncementsPage from './pages/announcements/index';
import AnnouncementsSettings from './pages/settings/Announcements';

// Site transfer page
import SiteTransferAcceptPage from './pages/site-transfers/[token]';
import CompanyMergeAcceptPage from './pages/company-merge/[token]';

// External page (공개 - 위임 작성)
import ExternalComposed from './pages/external/composed/[token]';

// Dev pages
import AiImageTestPage from './pages/dev/ai-image-test';

// Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import UsageExhaustedModal from './components/UsageExhaustedModal';
import { ToastContainer } from './components/ToastContainer';
import { WebSocketProvider } from './components/WebSocketProvider';
import { ScrollRestoration } from './components/ScrollRestoration';
import { InstallBanner } from './components/common/InstallBanner';
import RestoreConfirmModal from './components/RestoreConfirmModal';

// 공개 페이지 경로 접두사 (인증 초기화 불필요)
const PUBLIC_PATH_PREFIXES = [
  '/external/',
  '/approval/',
  '/share/',
  '/tbm/sign/',
  '/tbm/educator/',
  '/educations/tbm/sign/',
  '/educations/tbm/educator/',
  '/delete-approval/',
  '/invite/',
  '/release-notes',
  '/claim-company',
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// 구 TBM 라우트 → 신 /educations/tbm/* 리다이렉트 (쿼리스트링 보존)
function LegacyTbmRedirect({ build }: { build: (p: Readonly<Record<string, string | undefined>>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${build(params)}${search}`} replace />;
}

function App() {
  const { initialize, isLoading } = useAuthStore();

  // 앱 시작 시 인증 상태 초기화 (공개 페이지 제외)
  useEffect(() => {
    if (isPublicPath(window.location.pathname)) {
      // 공개 페이지에서는 인증 초기화 생략 → 로딩 해제만
      useAuthStore.setState({ isLoading: false });
      return;
    }
    initialize();
  }, [initialize]);

  // ToastContainer는 항상 한 번만 렌더링 (조건부 렌더링 밖에서)
  return (
    <>
      <ToastContainer />
      <InstallBanner />
      {/* 복원 확인 모달 — isLoading 로딩 분기 밖, Router 컨텍스트 안 */}
      <RestoreConfirmHost />
      {isLoading ? (
        // 초기 로딩 중
        <div className="min-h-screen flex items-center justify-center bg-ink-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
            <p className="mt-4 text-ink-600">로딩 중...</p>
          </div>
        </div>
      ) : (
        <QueryClientProvider client={queryClient}>
          <WebSocketProvider>
            <UsageExhaustedModal />
            <ScrollRestoration />
            <Routes>
      {/* 공개 라우트 */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/password-reset/verify" element={<PasswordResetVerify />} />
      {/* S27 예외 ③ — 탈퇴 통지의 복원 링크 랜딩. 로그인 없이 열려야 한다 */}
      <Route path="/account/restore" element={<AccountRestore />} />
      <Route path="/signup" element={<SignupTypeSelect />} />
      <Route path="/signup/username" element={<SignupUsername />} />
      <Route path="/signup/email" element={<SignupEmail />} />
      <Route path="/signup/email/complete" element={<SignupEmailComplete />} />
      <Route path="/invite/:token" element={<AcceptInvitation />} />

      {/* 온보딩 — 인증됐지만 멤버십 0개인 신규 가입자 전용 */}
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <OnboardingRoute />
          </PrivateRoute>
        }
      />

      {/* TBM 서명 (공개 - 인증 불필요) */}
      <Route path="/educations/tbm/sign/:token" element={<TBMSign />} />

      {/* TBM 담당자 전용 (공개 - 토큰 인증) */}
      <Route path="/educations/tbm/educator/:token" element={<EducatorPage />} />

      {/* 구경로 하위호환 리다이렉트 (이미 발송된 SMS/이메일 링크·인쇄된 QR·북마크 보호) */}
      <Route path="/tbm/sign/:token" element={<LegacyTbmRedirect build={(p) => `/educations/tbm/sign/${p.token}`} />} />
      <Route path="/tbm/educator/:token" element={<LegacyTbmRedirect build={(p) => `/educations/tbm/educator/${p.token}`} />} />

      {/* 외부 결재 (공개 - 토큰으로 인증) */}
      <Route path="/approval/:token" element={<ExternalApproval />} />

      {/* 삭제 승인 (공개 - 토큰으로 인증) */}
      <Route path="/delete-approval/:token" element={<DeleteApprovalPage />} />

      {/* 보고서 현장 담당자 서명 (공개 - SMS 링크 토큰) */}
      <Route path="/contact-signature/:token" element={<ContactSignaturePage />} />

      {/* COMPOSED 외부 위임 작성 (공개 - 현장 담당자 토큰) */}
      <Route path="/external/composed/:token" element={<ExternalComposed />} />

      {/* 외부 공유 보고서 (공개 - 토큰으로 조회) */}
      <Route path="/share/:token" element={<SharedReportPage />} />

      {/* 빙의 모드 진입 (공개 - 토큰으로 인증) */}
      <Route path="/auth/impersonate" element={<ImpersonatePage />} />

      {/* 피감업체 가입 전환 (공개 - 보고서 공유 링크 기반) */}
      <Route path="/claim-company" element={<ClaimCompany />} />

      {/* 업데이트 내역 (공개) — 화면 문구는 「업데이트 내역」이고 **경로·식별자는 `release-notes`로 둔다**
          (북마크·외부 링크가 걸려 있다. 2026-10-16 사용자 결정 — 바꾼 것은 사용자가 보는 문구뿐이다) */}
      <Route path="/release-notes" element={<ReleaseNotesPage />} />

      {/* 현장 이관 수락 (로그인 필요) */}
      <Route path="/site-transfers/:token" element={<SiteTransferAcceptPage />} />

      {/* 조직 통합 수락 (로그인 필요 — 대상 조직 소유자만) */}
      <Route path="/company-merge/:token" element={<CompanyMergeAcceptPage />} />

      {/* 홈 - 비로그인: 랜딩 페이지, 로그인: 대시보드 */}
      <Route
        path="/"
        element={<HomeOrDashboard />}
      />

      <Route
        path="/inspections"
        element={
          <PrivateRoute>
            <Layout>
              <InspectionsListAdaptive />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/inspections/new"
        element={
          <PrivateRoute>
            <Layout>
              <NewInspection />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/inspections/:id"
        element={
          <PrivateRoute>
            <Layout>
              <InspectionDetailAdaptive />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <Layout>
              <ReportsList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/reports/new"
        element={
          <PrivateRoute>
            <Layout>
              <NewReport />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 양식 전용 작성 라우트는 두지 않는다 — 아래 `/reports/new/composed/:templateKey` 하나다. */}
      <Route
        path="/reports/new/composed/:templateKey"
        element={
          <PrivateRoute>
            <Layout>
              <NewComposed />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 제출 후 흐름 — TBM 배포 설정(2026-09-06 §3·§5). 본문은 W4b, 진입 라우트가 먼저다
          (마지막 건 저장 직행·상세 리다이렉트가 이 href를 가리킴 — postSubmitFlows 레지스트리 정본). */}
      <Route
        path="/reports/:id/tbm-distribution-setup"
        element={
          <PrivateRoute>
            <Layout>
              <TbmDistributionSetupPage />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 보고서 미리보기: shareToken 파라미터 시 공개 접근 (Puppeteer PDF 캡처), 없으면 컴포넌트 내부에서 인증 체크 */}
      <Route
        path="/reports/:id/print-preview"
        element={
          <Suspense fallback={null}>
            <ComposedPrintPreview />
          </Suspense>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={null}>
                <ComposerReportDetail />
              </Suspense>
            </Layout>
          </PrivateRoute>
        }
      />

      {/* 구 `/tbm` 화면 주소의 내부 리다이렉트는 두지 않는다 — 폴백 라우트가 `/`로 떨어뜨린다.
          토큰 주소 두 개(`/tbm/sign/:token`·`/tbm/educator/:token`)만 이미 발송된 옛 SMS·QR 링크
          보호를 위해 위(공개 라우트 블록)에 남아 있다. */}
      <Route
        path="/educations/tbm/:id"
        element={
          <PrivateRoute>
            <TbmEducatePage />
          </PrivateRoute>
        }
      />

      <Route
        path="/schedules"
        element={
          <PrivateRoute>
            <Layout>
              <SchedulesPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/approvals"
        element={
          <PrivateRoute>
            <Layout>
              <ApprovalsPage />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* 법정 서류 — 메뉴(`Layout`의 flagKey)와 같은 visibility를 거울로 읽는다(LegalDocumentsGate doc) */}
      <Route
        path="/legal-documents"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <LegalDocumentsSitesPage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 우리 기관 서식(대행 기관의 서식 변형 저작) — 열림 판정은 서버(AgencyOverlaysPage doc) */}
      <Route
        path="/legal-documents/agency-overlays"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <AgencyOverlaysPage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />
      {/* ②-g3 G4 — 우리 기관의 법정 의무(조직 층 · 기관 종사자 직무교육). 열림 판정은 서버(기관 분류 ∧ 플래그 — 아니면 404) */}
      <Route
        path="/legal-documents/organization"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <OrganizationObligationsPage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/legal-documents/sites/:siteId"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <SiteObligationsPage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />
      {/* L32 — 계약이 끝난 현장의 지난 기록(읽기 전용 · 경로 정본 lib/legalDocumentsPast) */}
      <Route
        path="/legal-documents/past-sites/:siteId"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <PastSitePage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 알림 클릭 목적지 — 인스턴스로 현장을 찾아 의무 목록으로 replace(경로 정본 lib/legalDocumentRoutes) */}
      <Route
        path="/legal-documents/instances/:instanceId"
        element={
          <PrivateRoute>
            <Layout>
              <LegalDocumentsGate>
                <InstanceRedirectPage />
              </LegalDocumentsGate>
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <PrivateRoute>
            <Layout>
              <NotificationsPage />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* 공지 게시판 — 전사 + 조직 공지를 한자리에. **권한을 요구하지 않는다**(서버 뷰어
          컨트롤러도 그렇다 — 공지를 읽는 것은 구성원 모두의 일이다). 노출 축인 고정 띠는
          Layout의 배너 스택이 소유한다. */}
      <Route
        path="/announcements"
        element={
          <PrivateRoute>
            <Layout>
              <AnnouncementsPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/sites"
        element={
          <PrivateRoute>
            <Layout>
              <SitesList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/sites/new"
        element={
          <PrivateRoute>
            <Layout>
              <SiteForm />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/sites/:siteId"
        element={
          <PrivateRoute>
            <Layout>
              <SiteDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/sites/:siteId/edit"
        element={
          <PrivateRoute>
            <Layout>
              <SiteForm isEdit />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* 대행 현장 연결·요청(P4) — 플래그 `agency-site-link`는 2026-11-01에 걷었다. 요청 단건 경로는 알림 목적지(lib/agencySiteLinkView) */}
      <Route
        path="/agency-links/connect"
        element={
          <PrivateRoute>
            <Layout>
              <AgencyLinkConnectPage />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/agency-link-requests"
        element={
          <PrivateRoute>
            <Layout>
              <AgencyLinkRequestsPage />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/agency-link-requests/:requestId"
        element={
          <PrivateRoute>
            <Layout>
              <AgencyLinkRequestDetailPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/members"
        element={
          <PrivateRoute>
            <Layout>
              <MembersPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/organization-info"
        element={
          <PrivateRoute>
            <Layout>
              <OrganizationInfo />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* 기존 경로 호환성 - /settings/account로 리다이렉트 */}
      <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
      <Route
        path="/settings/account"
        element={
          <PrivateRoute>
            <Layout>
              <AccountSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/organization"
        element={
          <PrivateRoute>
            <Layout>
              <OrganizationSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 기존 경로 호환성 - /members로 리다이렉트 */}
      <Route path="/settings/members" element={<Navigate to="/members" replace />} />
      <Route
        path="/settings/groups"
        element={
          <PrivateRoute>
            <Layout>
              <GroupsSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/approval"
        element={
          <PrivateRoute>
            <Layout>
              <ApprovalSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/roles"
        element={
          <PrivateRoute>
            <Layout>
              <RolesSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 조직 공지 저작 — 진입 게이트는 `announcement.manage`이고 내비 항목이 **같은 키**를
          읽는다(`layoutNavVisibility.ts`의 거울 규칙: 넓으면 막다른 길, 좁으면 dead grant). */}
      <Route
        path="/settings/announcements"
        element={
          <PrivateRoute>
            <Layout>
              <AnnouncementsSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <PrivateRoute>
            <Layout>
              <BillingSettings />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* 기존 경로 호환성 - /profile로 리다이렉트 */}
      <Route path="/settings/profile" element={<Navigate to="/profile" replace />} />

      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* Dev/PoC 페이지 (인증 필요) */}
      <Route
        path="/dev/ai-image-test"
        element={
          <PrivateRoute>
            <AiImageTestPage />
          </PrivateRoute>
        }
      />

      {/* 404 - 잘못된 경로는 대시보드로 리다이렉트 */}
      <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </WebSocketProvider>
        </QueryClientProvider>
      )}
    </>
  );
}

// 복원 확인 모달 호스트 — isLoading 로딩화면 분기 밖, Router(BrowserRouter) 컨텍스트 안에 배치
// Login 로컬 state 대신 전역 store 구독 → isLoading으로 인한 Login 언마운트 시에도 유실 없음
function RestoreConfirmHost() {
  const { restorePending, restore, clearRestorePending } = useAuthStore();
  const { showToast } = useToastStore();
  const navigate = useNavigate();

  if (!restorePending) return null;

  const handleRestore = async (restoreToken: string) => {
    await restore(restoreToken);
    // restore() 내부에서 restorePending = null 처리
    showToast('계정이 복원되었습니다. 다시 오신 것을 환영합니다!', 'success');
    navigate('/', { replace: true });
  };

  const handleCancel = () => {
    clearRestorePending();
  };

  return (
    <RestoreConfirmModal
      isOpen={true}
      daysLeft={restorePending.daysLeft}
      restoreToken={restorePending.restoreToken}
      onRestore={handleRestore}
      onCancel={handleCancel}
    />
  );
}

// 온보딩 가드 — 멤버십 0개 전용 (조직 보유 시 대시보드로)
function OnboardingRoute() {
  const { user } = useAuthStore();
  if (user && user.memberships.length > 0) {
    return <Navigate to="/" replace />;
  }
  return <Onboarding />;
}

// 홈 또는 대시보드 (인증 상태에 따라 분기)
function HomeOrDashboard() {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    // 활성 멤버십(조직) 0개 → 온보딩 유도. '/'는 PrivateRoute로 감싸이지 않으므로
    // PrivateRoute와 동일한 0-조직 정책을 여기서도 적용 (가입 직후 navigate('/') 포함).
    if (user && user.memberships.length === 0) {
      return <Navigate to="/onboarding" replace />;
    }
    return (
      <Layout>
        <Dashboard />
      </Layout>
    );
  }

  return <Home />;
}

export default App;
